import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FileText, ShieldOff, Lock, Loader2, MapPin, Navigation, ExternalLink, Info } from "lucide-react";

/**
 * Public, no-auth page rendering a vendor's policies in a FAQ accordion (T&C, cancellation,
 * privacy). FAQPage JSON-LD schema is injected into the head so search engines surface each
 * policy as a rich FAQ result. Default-platform copy is clearly tagged on each section so
 * renters know whether the host has authored their own or relies on RentalCar.al defaults.
 *
 * Logistics block: above the legal accordions, renders the host's pickup + drop-off
 * instructions and (when given) an embedded Google Maps mini-map. Only `google.com/maps`
 * and `maps.google.com` URLs are allowed in the iframe — any other domain is rendered as a
 * plain external link to keep the page sandboxed.
 */
const POLICY_META = {
  terms_and_conditions: { label: "Terms & Conditions", icon: FileText },
  cancellation_policy: { label: "Cancellation & Refund", icon: ShieldOff },
  privacy_policy: { label: "Privacy Policy", icon: Lock },
};

// Defensive parser: we only iframe `google.com/maps/embed*` URLs because Google blocks the
// regular share URLs (`maps.app.goo.gl`, `/maps/place/…`) with X-Frame-Options=SAMEORIGIN.
// Anything else falls back to a clickable link.
function _resolveMapUrl(raw) {
  if (!raw || typeof raw !== "string") return { ok: false };
  let url;
  try { url = new URL(raw.trim()); } catch { return { ok: false }; }
  if (!["http:", "https:"].includes(url.protocol)) return { ok: false };
  const host = url.host.toLowerCase();
  const isGoogleMaps = host === "google.com" || host.endsWith(".google.com") || host === "maps.google.com";
  const isEmbed = isGoogleMaps && /\/maps\/embed/i.test(url.pathname);
  return { ok: true, isEmbed, isGoogleMaps, href: url.toString() };
}

export default function VendorPoliciesPage() {
  const { vendor_id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/vendors/${vendor_id}/policies`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load policies.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [vendor_id]);

  // Inject FAQPage JSON-LD for SEO. Cleans up on unmount + on data change.
  useEffect(() => {
    if (!data) return;
    const faqJsonLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": Object.entries(data.policies).map(([key, value]) => ({
        "@type": "Question",
        "name": `${POLICY_META[key].label} — ${data.vendor_name}`,
        "acceptedAnswer": { "@type": "Answer", "text": value.content },
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(faqJsonLd);
    script.setAttribute("data-faq-vendor", data.vendor_id);
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [data]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto px-6 lg:px-10 py-12 w-full" data-testid="vendor-policies-page">
        {loading && (
          <div className="flex items-center justify-center py-24 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading policies…</div>
        )}
        {!loading && error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-6 text-rose-800 text-sm" data-testid="vendor-policies-error">
            {error}
          </div>
        )}
        {!loading && data && (
          <>
            <div className="flex items-center gap-4 mb-8">
              {data.business_logo && (
                <img src={data.business_logo} alt={data.vendor_name} className="h-14 w-14 rounded-2xl object-cover border border-border" />
              )}
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 inline-flex items-center gap-1">
                  <Info className="h-3 w-3" /> Important info
                </div>
                <h1 className="font-display text-3xl lg:text-4xl tracking-tight font-medium" data-testid="vendor-policies-title">{data.vendor_name}</h1>
              </div>
            </div>
            <p className="text-sm text-neutral-500 mb-8 max-w-2xl">
              The terms, refund conditions, and data-handling rules that apply when you rent from this host. Tap a section to expand.
            </p>

            {data.logistics && (data.logistics.pickup_instructions || data.logistics.pickup_map_url || data.logistics.dropoff_instructions || data.logistics.dropoff_map_url) && (
              <div className="mb-10 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="vendor-logistics-block">
                <LogisticsCard
                  testId="pickup"
                  title="Pickup"
                  icon={<Navigation className="h-4 w-4 text-emerald-700" />}
                  accent="bg-emerald-50 border-emerald-200"
                  instructions={data.logistics.pickup_instructions}
                  mapUrl={data.logistics.pickup_map_url}
                />
                <LogisticsCard
                  testId="dropoff"
                  title="Drop-off"
                  icon={<MapPin className="h-4 w-4 text-rose-700" />}
                  accent="bg-rose-50 border-rose-200"
                  instructions={data.logistics.dropoff_instructions}
                  mapUrl={data.logistics.dropoff_map_url}
                />
              </div>
            )}

            {/* Iter 67dg — Full rental terms (structured 8-section block).
                Renders only the sections the vendor filled in. */}
            {data.rental_terms && Object.values(data.rental_terms).some((v) => v) && (
              <section className="mb-10" data-testid="vendor-rental-terms-block">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4 text-blue-700" />
                  <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-800">Full rental terms</h2>
                </div>
                <Accordion type="multiple" className="space-y-2.5">
                  {[
                    ["terms_included",            "Included in the rental price",  "text-emerald-700", "bg-emerald-50"],
                    ["terms_not_included",        "Not included in the rental price","text-rose-700",  "bg-rose-50"],
                    ["terms_pickup_requirements", "What you need at Pick-up",       "text-amber-700",  "bg-amber-50"],
                    ["terms_deposits_excess",     "Deposits, excess and cover",     "text-indigo-700", "bg-indigo-50"],
                    ["terms_fuel_policy",         "Fuel policy",                     "text-sky-700",    "bg-sky-50"],
                    ["terms_extra_services",      "Extra services (payable at counter)","text-violet-700","bg-violet-50"],
                    ["terms_extra_equipment",     "Extra Equipment",                "text-teal-700",   "bg-teal-50"],
                    ["terms_important_info",      "Important information",          "text-rose-700",   "bg-rose-50"],
                  ]
                    .filter(([k]) => (data.rental_terms[k] || "").trim().length > 0)
                    .map(([k, label, tone, bg]) => (
                      <AccordionItem
                        key={k} value={k}
                        className="rounded-2xl border border-border bg-white px-4 data-[state=open]:bg-neutral-50/60"
                        data-testid={`rental-terms-${k}`}
                      >
                        <AccordionTrigger className="hover:no-underline py-3">
                          <div className="flex items-center gap-3 text-left">
                            <div className={`h-8 w-8 rounded-xl ${bg} flex items-center justify-center shrink-0 ${tone}`}>
                              <FileText className="h-3.5 w-3.5" />
                            </div>
                            <div className="font-medium text-sm">{label}</div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap pb-4 pt-1 pl-11">
                          {data.rental_terms[k]}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                </Accordion>
              </section>
            )}

            <Accordion type="multiple" className="space-y-3" defaultValue={["terms_and_conditions"]}>
              {Object.entries(data.policies).map(([key, value]) => {
                const Meta = POLICY_META[key];
                const Icon = Meta.icon;
                return (
                  <AccordionItem key={key} value={key} className="rounded-2xl border border-border bg-white px-4 data-[state=open]:bg-neutral-50/60" data-testid={`policy-section-${key}`}>
                    <AccordionTrigger className="hover:no-underline py-4">
                      <div className="flex items-center gap-3 text-left">
                        <div className="h-9 w-9 rounded-2xl bg-blue-50 flex items-center justify-center shrink-0">
                          <Icon className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-base">{Meta.label}</div>
                          {value.is_default && (
                            <div className="text-[10px] uppercase tracking-wider text-amber-700 mt-0.5">RentalCar.al default</div>
                          )}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap pb-5 pt-1 pl-12">
                      {value.content}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
            <div className="mt-10 text-xs text-neutral-500">
              Questions? Reach out to <span className="font-medium text-neutral-700">{data.vendor_name}</span> via the messaging tab on your booking, or{" "}
              <Link to="/contact" className="text-blue-600 hover:underline">contact RentalCar.al support</Link>.
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function LogisticsCard({ testId, title, icon, accent, instructions, mapUrl }) {
  if (!instructions && !mapUrl) return null;
  const map = _resolveMapUrl(mapUrl);
  return (
    <div className={`rounded-2xl border ${accent} p-4`} data-testid={`logistics-${testId}`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-800">{title}</h3>
      </div>
      {instructions && (
        <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap" data-testid={`logistics-${testId}-instructions`}>
          {instructions}
        </p>
      )}
      {map.ok && map.isEmbed && (
        <iframe
          src={map.href}
          title={`${title} location map`}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
          className="mt-3 w-full h-56 rounded-xl border border-border"
          data-testid={`logistics-${testId}-map`}
        />
      )}
      {map.ok && !map.isEmbed && (
        <a
          href={map.href}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:underline"
          data-testid={`logistics-${testId}-link`}
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {map.isGoogleMaps ? "Open in Google Maps" : "Open map"}
        </a>
      )}
    </div>
  );
}
